/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { NavigationViewRemote } from '@jira/polaris__component-navigation-store/src/TypesRemoteViewRemover.tsx';
import { fireOperationalAnalyticsDeferred } from '@atlassian/jira-product-analytics-bridge';
import {
	fetchCollectionsViews,
	fetchView,
} from '@jira/polaris__component-jpd-views-service/src/fetchProjectsViews.tsx';
import { cloneView } from '@jira/polaris__component-jpd-views-service/src/cloneView.tsx';
import { createView } from '@jira/polaris__component-jpd-views-service/src/createView.tsx';
import { deleteView } from '@jira/polaris__component-jpd-views-service/src/deleteView.tsx';
import { updateView } from '@jira/polaris__component-jpd-views-service/src/updateView.tsx';
import { collectionRankView } from '@jira/polaris__component-jpd-views-service/src/rankView.tsx';
import { convertViewTypeToRemote, transformView, transformViews } from './transform/index.tsx';

export const createNavigationViewRemote = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): NavigationViewRemote => ({
	// Fetch all views for a specific collection
	fetch: async (collectionUUID: string) => {
		const startAt = Date.now();
		try {
			const views = await fetchCollectionsViews(collectionUUID).then(transformViews);
			fireOperationalAnalyticsDeferred(
				createAnalyticsEvent({
					duration: Date.now() - startAt,
				}),
				'fetchRoadmapsNavigationViews success',
			);
			return views;
		} catch (error) {
			fireOperationalAnalyticsDeferred(
				createAnalyticsEvent({
					duration: Date.now() - startAt,
				}),
				'fetchRoadmapsNavigationViews failure',
			);
			throw error;
		}
	},
	fetchView: async (viewUUID) => fetchView(viewUUID).then(transformView),
	createView: async ({ viewType, ...rest }) =>
		createView({
			viewType: convertViewTypeToRemote(viewType),
			...rest,
		}).then(transformView),
	updateView: async ({ id, viewType, name, emoji }) => {
		const params = {
			viewType: convertViewTypeToRemote(viewType),
			name,
			emoji,
		};
		return updateView(id, params).then(transformView);
	},
	cloneView: async ({ id, viewType, name, emoji }) => {
		const params = {
			viewType: convertViewTypeToRemote(viewType),
			name,
			emoji,
		};
		return cloneView(id, params).then(transformView);
	},
	rankView: async ({ id, rankIndex, parentId, containerId }) =>
		collectionRankView({
			collectionUUID: containerId,
			parentUUID: parentId,
			rank: rankIndex,
			viewUUID: id,
		}),
	deleteView: async ({ viewId }) => deleteView(viewId),
});
